import React from "react";
import { useOptixFlowConfig } from "../core/OptixFlowContext";
import { IMAGE_GUARD_STYLE, mergeGuardStyle } from "../utils/width-guard";

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  optixFlowConfig?: any; // Accept optixFlowConfig as direct prop (overrides context)
}

/**
 * Image component that optionally delegates to @page-speed/img if available
 * Falls back to standard img tag if @page-speed/img is not installed
 *
 * Supports OptixFlow configuration via:
 * 1. Direct prop: <Image optixFlowConfig={{...}} />
 * 2. Context: <Markdown optixFlowConfig={{...}}><img /></Markdown>
 */
export function Image({
  src,
  alt = "",
  optixFlowConfig: propConfig,
  style,
  ...props
}: ImageProps) {
  // Get optixFlowConfig from context (can be overridden by prop)
  const contextConfig = useOptixFlowConfig();
  const optixFlowConfig = propConfig || contextConfig;

  /**
   * An image's intrinsic width is unbounded and raw HTML in markdown can carry a
   * hard-coded `width` attribute; either can widen the article column on a phone
   * and drag the whole layout past the viewport. Capped inline so the guard
   * survives a safelist-compiled Tailwind build.
   */
  const guardedStyle = mergeGuardStyle(IMAGE_GUARD_STYLE, style);

  // Try to import @page-speed/img dynamically
  // If not available, fall back to standard img
  const [PageSpeedImg, setPageSpeedImg] = React.useState<React.ComponentType<any> | null>(null);
  const [useFallback, setUseFallback] = React.useState(false);

  React.useEffect(() => {
    // Only try to load once
    if (PageSpeedImg || useFallback) return;

    // The dynamic import can settle after this component unmounts (a short-lived
    // markdown render, or a test tearing down its environment). Setting state
    // then is a no-op at best and throws inside React's scheduler at worst, so
    // the resolution is dropped once unmounted.
    let active = true;

    import("@page-speed/img")
      .then((mod) => {
        if (active) setPageSpeedImg(() => mod.Img);
      })
      .catch(() => {
        // @page-speed/img not available, use fallback
        if (active) setUseFallback(true);
      });

    return () => {
      active = false;
    };
  }, [PageSpeedImg, useFallback]);

  // Use @page-speed/img if available
  if (PageSpeedImg) {
    return (
      <PageSpeedImg
        src={src}
        alt={alt}
        optixFlowConfig={optixFlowConfig}
        style={guardedStyle}
        {...props}
      />
    );
  }

  // Fallback to standard img with loading="lazy"
  return (
    <img src={src} alt={alt} loading="lazy" style={guardedStyle} {...props} />
  );
}
